import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { RestrictionsListItem } from "@dtm-frontend/dss-shared-lib";
import { Page } from "@dtm-frontend/shared/ui";
import { AnimationUtils, LocalComponentStore } from "@dtm-frontend/shared/utils";
import { DateFormatOptions } from "@jsverse/transloco-locale";
import { RestrictionsTableColumns } from "../../models/flight-zone-management.models";
import { NotamModalService } from "../../services/notam-modal.service";

interface RestrictionsTableComponentState {
    isProcessing: boolean;
    hasDataRetrievalError: boolean;
    restrictions: RestrictionsListItem[];
    expandedElement: RestrictionsListItem | null;
    displayedColumns: RestrictionsTableColumns;
    applicationsPage: Page | undefined;
    hasSupervisorPermissions: boolean;
}

@Component({
    selector: "dss-admin-lib-restrictions-table[restrictions][displayedColumns]",
    templateUrl: "./restrictions-table.component.html",
    styleUrls: ["./restrictions-table.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
    animations: [AnimationUtils.slideInAnimation()],
})
export class RestrictionsTableComponent {
    protected readonly localizeDateConfig: DateFormatOptions = {
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        timeZone: "UTC",
        timeZoneName: "short",
    };
    protected readonly restrictions$ = this.localStore.selectByKey("restrictions");
    protected readonly isProcessing$ = this.localStore.selectByKey("isProcessing");
    protected readonly hasDataRetrievalError$ = this.localStore.selectByKey("hasDataRetrievalError");
    protected readonly expandedElement$ = this.localStore.selectByKey("expandedElement");
    protected readonly displayedColumns$ = this.localStore.selectByKey("displayedColumns");
    protected readonly applicationsPage$ = this.localStore.selectByKey("applicationsPage");
    protected readonly hasSupervisorPermissions$ = this.localStore.selectByKey("hasSupervisorPermissions");

    @Input()
    public set restrictions(value: RestrictionsListItem[] | undefined) {
        this.localStore.patchState({ restrictions: value ?? [] });
    }

    @Input()
    public set isProcessing(value: BooleanInput) {
        this.localStore.patchState({ isProcessing: coerceBooleanProperty(value) });
    }

    @Input()
    public set hasDataRetrievalError(value: BooleanInput) {
        this.localStore.patchState({ hasDataRetrievalError: coerceBooleanProperty(value) });
    }

    @Input()
    public set displayedColumns(value: RestrictionsTableColumns) {
        this.localStore.patchState({ displayedColumns: value });
    }

    @Input()
    public set page(value: Page | undefined) {
        this.localStore.patchState({ applicationsPage: value });
    }

    @Input()
    public set hasSupervisorPermissions(value: BooleanInput) {
        this.localStore.patchState({ hasSupervisorPermissions: coerceBooleanProperty(value) });
    }

    @Output() protected readonly pageChange = new EventEmitter<PageEvent>();
    @Output() protected readonly listRefresh = new EventEmitter<void>();
    @Output() protected readonly kmlDownload = new EventEmitter<RestrictionsListItem>();

    constructor(
        private readonly localStore: LocalComponentStore<RestrictionsTableComponentState>,
        private readonly notamModalService: NotamModalService
    ) {
        this.localStore.setState({
            restrictions: [],
            isProcessing: false,
            hasDataRetrievalError: false,
            expandedElement: null,
            displayedColumns: [],
            applicationsPage: undefined,
            hasSupervisorPermissions: false,
        });
    }

    protected toggleExpandableRow(row: RestrictionsListItem): void {
        const currentlyExpandedElement = this.localStore.selectSnapshotByKey("expandedElement");

        this.localStore.patchState({ expandedElement: currentlyExpandedElement === row ? null : row });
    }

    protected openNotamPreview(flightZoneId: string): void {
        this.notamModalService.openNotamPreview(flightZoneId);
    }
}
